/**
 * Shared filter bar: dropdowns, selected pills, Clear All, mobile accordion panel.
 * Pages supply group keys and an apply callback; search/url sync are optional.
 */

// Trim, collapse runs of whitespace and fold case, matching normalizeForMatch in
// modules/swatch-detail.js. Only resolveOptionValue uses it; the option lookups stay exact.
const normalizeOptionValue = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const cssEscape = (value) => {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }
  return String(value).replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
};

const createSharedFilterBar = (config) => {
  const {
    idPrefix = 'filter',
    groupKeys = [],
    onFiltersChange,
    onMobilePendingChange = null,
    urlSync = false,
    search = null,
  } = config;

  const root = document.querySelector(`[data-filter-bar="${idPrefix}"]`);
  const mobilePanel = document.querySelector(`[data-filter-bar-panel="${idPrefix}"]`);
  const mobileFilterBtn = document.querySelector(`[data-filter-bar-open="${idPrefix}"]`);
  const mobileFilterClose = document.querySelector(`[data-filter-bar-close="${idPrefix}"]`);
  const mobileFilterApply = document.querySelector(`[data-filter-bar-apply="${idPrefix}"]`);
  const clearFiltersDesktop = document.querySelector(`[data-filter-bar-clear-desktop="${idPrefix}"]`);
  const clearFiltersMobile = document.querySelector(`[data-filter-bar-clear-mobile="${idPrefix}"]`);
  const selectedFiltersContainer = document.querySelector(`[data-filter-bar-selected="${idPrefix}"]`);
  const desktopMenu = document.querySelector(`[data-filter-bar-desktop="${idPrefix}"]`);

  const desktopFilterOptions = desktopMenu
    ? Array.prototype.slice.call(desktopMenu.querySelectorAll('.filter-bar-option'))
    : [];
  const mobileFilterOptions = mobilePanel
    ? Array.prototype.slice.call(mobilePanel.querySelectorAll('.filter-bar-option-mobile'))
    : [];

  const searchInput = search
    ? document.querySelector(`[data-filter-bar-search-input="${idPrefix}"]`)
    : null;
  const searchForm = search
    ? document.querySelector(`[data-filter-bar-search-form="${idPrefix}"]`)
    : null;
  const searchClearBtn = search
    ? document.querySelector(`[data-filter-bar-search-clear="${idPrefix}"]`)
    : null;
  const searchIcon = search
    ? document.querySelector(`[data-filter-bar-search-icon="${idPrefix}"]`)
    : null;

  if (!root) {
    return null;
  }

  if (mobilePanel && mobilePanel.parentElement !== document.body) {
    document.body.appendChild(mobilePanel);
  }

  if (desktopMenu) {
    Array.prototype.slice.call(desktopMenu.querySelectorAll('.dropdown-menu')).forEach((menu) => {
      menu.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });
  }

  const emptyFilters = () => {
    const next = {};
    groupKeys.forEach((key) => {
      next[key] = [];
    });
    return next;
  };

  let filters = emptyFilters();
  let searchMatchIds = null;
  let searchRequestId = 0;
  let searchDebounceTimer = null;
  const searchDebounceMs = 150;

  const getDesktopFilterOption = (filterType, value) => (
    desktopMenu
      ? desktopMenu.querySelector(`.filter-bar-option[data-filter="${filterType}"][value="${cssEscape(value)}"]`)
      : null
  );

  const getMobileFilterOption = (filterType, value) => (
    mobilePanel
      ? mobilePanel.querySelector(`.filter-bar-option-mobile[data-filter="${filterType}"][value="${cssEscape(value)}"]`)
      : null
  );

  const resolveOptionValue = (filterType, value) => {
    const wanted = normalizeOptionValue(value);

    if (!wanted) {
      return '';
    }

    for (let i = 0; i < desktopFilterOptions.length; i += 1) {
      const option = desktopFilterOptions[i];

      if (option.getAttribute('data-filter') === filterType
        && normalizeOptionValue(option.value) === wanted) {
        return option.value;
      }
    }

    return '';
  };

  const getFilterOptionLabel = (input) => {
    if (!input) {
      return '';
    }

    if (input.id) {
      const labelEl = document.querySelector(`label[for="${cssEscape(input.id)}"]`);
      if (labelEl) {
        return labelEl.textContent.trim();
      }
    }

    const formCheck = input.closest('.form-check');
    const siblingLabel = formCheck ? formCheck.querySelector('.form-check-label') : null;
    return siblingLabel ? siblingLabel.textContent.trim() : '';
  };

  const buildFiltersFromCheckboxes = (checkboxes) => {
    const nextFilters = emptyFilters();

    checkboxes.forEach((input) => {
      if (input.checked && nextFilters[input.dataset.filter]) {
        nextFilters[input.dataset.filter].push(input.value);
      }
    });

    return nextFilters;
  };

  const syncMobileCheckboxesFromFilters = (nextFilters) => {
    mobileFilterOptions.forEach((mobileInput) => {
      const list = nextFilters[mobileInput.dataset.filter] || [];
      mobileInput.checked = list.indexOf(mobileInput.value) !== -1;
    });
  };

  const rebuildFiltersFromDesktop = () => {
    filters = buildFiltersFromCheckboxes(desktopFilterOptions);
  };

  const syncPendingFromApplied = () => {
    syncMobileCheckboxesFromFilters(filters);
  };

  const getPendingMobileFilters = () => buildFiltersFromCheckboxes(mobileFilterOptions);

  const notifyMobilePendingChange = () => {
    if (typeof onMobilePendingChange === 'function') {
      onMobilePendingChange(getPendingMobileFilters());
    }
  };

  const applyPendingMobileFilters = () => {
    mobileFilterOptions.forEach((mobileInput) => {
      const desktopInput = getDesktopFilterOption(mobileInput.dataset.filter, mobileInput.value);
      if (desktopInput) {
        desktopInput.checked = mobileInput.checked;
      }
    });

    filters = buildFiltersFromCheckboxes(mobileFilterOptions);
  };

  const clearAllFilterCheckboxes = () => {
    desktopFilterOptions.forEach((input) => {
      input.checked = false;
    });
    mobileFilterOptions.forEach((input) => {
      input.checked = false;
    });
  };

  const hasActiveCategoryFilters = () => (
    groupKeys.some(key => (filters[key] || []).length > 0)
  );

  const updateClearAllVisibility = () => {
    const showClearAll = hasActiveCategoryFilters();

    if (clearFiltersDesktop) {
      clearFiltersDesktop.classList.toggle('d-none', !showClearAll);
    }

    if (clearFiltersMobile) {
      clearFiltersMobile.classList.toggle('d-none', !showClearAll);
    }
  };

  const getSelectedFilters = () => {
    const selected = [];
    const seen = {};

    groupKeys.forEach((groupKey) => {
      (filters[groupKey] || []).forEach((id) => {
        const key = `${groupKey}:${id}`;
        if (seen[key]) {
          return;
        }

        const input = getDesktopFilterOption(groupKey, id);
        if (!input) {
          return;
        }

        const label = getFilterOptionLabel(input);
        if (!label) {
          return;
        }

        seen[key] = true;
        selected.push({
          groupKey,
          id,
          value: id,
          label,
        });
      });
    });

    return selected;
  };

  const buildFilterQueryString = () => {
    const params = new URLSearchParams();

    groupKeys.forEach((groupKey) => {
      const values = (filters[groupKey] || [])
        .map(value => String(value).trim())
        .filter((value, index, list) => value && list.indexOf(value) === index);

      if (values.length > 0) {
        params.set(groupKey, values.join(','));
      }
    });

    if (searchInput) {
      const searchText = (searchInput.value || '').trim();
      if (searchText) {
        params.set('q', searchText);
      }
    }

    return params.toString();
  };

  // The query keys this bar owns and rewrites whenever filters change.
  const getOwnedParamKeys = () => {
    const keys = groupKeys.slice();
    if (searchInput) {
      keys.push('q');
    }
    return keys;
  };

  const syncListingUrl = () => {
    if (!urlSync) {
      return;
    }

    // Only the owned keys are replaced. Everything else already on the URL is carried
    // through: utm_*, the crm_lead_id that klaviyo/lead-id.js appends, and the swatch
    // detail deep link. Rebuilding the query from scratch used to drop all of them on
    // the first filter change.
    const params = new URLSearchParams(window.location.search);
    getOwnedParamKeys().forEach((key) => {
      params.delete(key);
    });

    const filterParams = new URLSearchParams(buildFilterQueryString());
    filterParams.forEach((value, key) => {
      params.set(key, value);
    });

    const query = params.toString();
    const { pathname, hash } = window.location;
    const nextUrl = (query ? `${pathname}?${query}` : pathname) + hash;

    if (nextUrl !== pathname + window.location.search + hash) {
      window.history.replaceState(null, '', nextUrl);
    }
  };

  const restoreFiltersFromQuery = () => {
    if (!urlSync) {
      return false;
    }

    // URL is the source of truth — ignore any browser-restored checkbox state.
    clearAllFilterCheckboxes();
    filters = emptyFilters();

    const params = new URLSearchParams(window.location.search);
    let restored = false;

    groupKeys.forEach((groupKey) => {
      const raw = params.get(groupKey);
      if (!raw) {
        return;
      }

      raw.split(',').forEach((part) => {
        const value = part.trim();
        if (!value) {
          return;
        }

        const desktopInput = getDesktopFilterOption(groupKey, value);
        const mobileInput = getMobileFilterOption(groupKey, value);
        if (desktopInput) {
          desktopInput.checked = true;
          restored = true;
        }
        if (mobileInput) {
          mobileInput.checked = true;
        }
      });
    });

    if (searchInput) {
      const searchText = params.get('q');
      if (searchText) {
        searchInput.value = searchText;
        restored = true;
      } else {
        searchInput.value = '';
      }
    }

    rebuildFiltersFromDesktop();
    syncPendingFromApplied();

    return restored;
  };

  const emitChange = () => {
    const meta = {
      search: searchInput ? (searchInput.value || '').trim() : '',
      searchMatchIds,
      queryString: buildFilterQueryString(),
    };

    if (typeof onFiltersChange === 'function') {
      onFiltersChange(filters, meta);
    }

    // eslint-disable-next-line no-use-before-define
    renderSelectedFilters();
    updateClearAllVisibility();
    syncListingUrl();
  };

  const removeSelectedFilter = (groupKey, value) => {
    const desktopInput = getDesktopFilterOption(groupKey, value);
    const mobileInput = getMobileFilterOption(groupKey, value);

    if (desktopInput) {
      desktopInput.checked = false;
    }

    if (mobileInput) {
      mobileInput.checked = false;
    }

    if (filters[groupKey]) {
      filters[groupKey] = filters[groupKey].filter(v => v !== value);
    }

    syncPendingFromApplied();
    emitChange();
  };

  const renderSelectedFilters = () => {
    if (!selectedFiltersContainer) {
      return;
    }

    const selected = getSelectedFilters();
    selectedFiltersContainer.replaceChildren();

    if (selected.length === 0) {
      selectedFiltersContainer.classList.add('d-none');
      return;
    }

    selectedFiltersContainer.classList.remove('d-none');

    selected.forEach(({ groupKey, id, label }) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'filter-bar-selected-btn btn rounded-pill bg-white text-dark d-inline-flex align-items-center';
      button.dataset.filterGroup = groupKey;
      button.dataset.filterId = id;
      button.setAttribute('aria-label', `Remove ${label} filter`);

      const closeIcon = document.createElement('span');
      closeIcon.className = 'filter-bar-selected-remove flex-shrink-0';
      closeIcon.setAttribute('aria-hidden', 'true');
      closeIcon.textContent = '\u00D7';

      const labelEl = document.createElement('span');
      labelEl.className = 'filter-bar-selected-label text-decoration-underline';
      labelEl.textContent = label;

      button.append(closeIcon, labelEl);
      button.addEventListener('click', () => {
        removeSelectedFilter(groupKey, id);
      });

      selectedFiltersContainer.appendChild(button);
    });
  };

  const clearAppliedFilters = () => {
    clearAllFilterCheckboxes();
    filters = emptyFilters();
    emitChange();
  };

  const collapseMobileAccordions = () => {
    if (!mobilePanel) {
      return;
    }

    Array.prototype.slice.call(mobilePanel.querySelectorAll('.collapse.show')).forEach((el) => {
      if (window.bootstrap && window.bootstrap.Collapse) {
        window.bootstrap.Collapse.getOrCreateInstance(el, { toggle: false }).hide();
        return;
      }

      el.classList.remove('show', 'collapsing');
      el.style.height = '';
    });

    const openAccordionBtns = mobilePanel.querySelectorAll('.filter-bar-mobile-accordion-btn[aria-expanded="true"]');
    Array.prototype.slice.call(openAccordionBtns).forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
      btn.classList.add('collapsed');
    });
  };

  const openMobilePanel = () => {
    if (!mobilePanel) {
      return;
    }
    syncPendingFromApplied();
    notifyMobilePendingChange();
    collapseMobileAccordions();
    mobilePanel.classList.add('toggled');
    mobilePanel.setAttribute('aria-hidden', 'false');
    if (mobileFilterBtn) {
      mobileFilterBtn.setAttribute('aria-expanded', 'true');
    }
    document.body.classList.add('overflow-hidden');
  };

  const closeMobilePanel = () => {
    if (!mobilePanel) {
      return;
    }
    collapseMobileAccordions();
    mobilePanel.classList.remove('toggled');
    mobilePanel.setAttribute('aria-hidden', 'true');
    if (mobileFilterBtn) {
      mobileFilterBtn.setAttribute('aria-expanded', 'false');
    }
    document.body.classList.remove('overflow-hidden');
  };

  if (mobileFilterBtn) {
    mobileFilterBtn.addEventListener('click', openMobilePanel);
  }

  if (mobileFilterClose) {
    mobileFilterClose.addEventListener('click', () => {
      syncPendingFromApplied();
      notifyMobilePendingChange();
      closeMobilePanel();
    });
  }

  if (mobileFilterApply) {
    mobileFilterApply.addEventListener('click', () => {
      applyPendingMobileFilters();
      emitChange();
      closeMobilePanel();
    });
  }

  if (clearFiltersDesktop) {
    clearFiltersDesktop.addEventListener('click', clearAppliedFilters);
  }

  if (clearFiltersMobile) {
    clearFiltersMobile.addEventListener('click', () => {
      clearAppliedFilters();
      closeMobilePanel();
    });
  }

  mobileFilterOptions.forEach((input) => {
    input.addEventListener('change', (e) => {
      e.stopPropagation();
      notifyMobilePendingChange();
    });
  });

  desktopFilterOptions.forEach((input) => {
    input.addEventListener('change', (e) => {
      const el = e.currentTarget;
      const type = el.dataset.filter;
      const { value } = el;

      if (!filters[type]) {
        filters[type] = [];
      }

      if (el.checked) {
        if (filters[type].indexOf(value) === -1) {
          filters[type].push(value);
        }
      } else {
        filters[type] = filters[type].filter(v => v !== value);
      }

      syncPendingFromApplied();
      emitChange();
    });
  });

  const updateSearchClearVisibility = () => {
    if (!searchInput) {
      return;
    }

    const hasText = (searchInput.value || '').length > 0;

    if (searchClearBtn) {
      searchClearBtn.classList.toggle('d-none', !hasText);
    }

    if (searchIcon) {
      searchIcon.classList.toggle('d-none', hasText);
    }
  };

  const runSearchAndApply = (immediate) => {
    if (!search || !searchInput) {
      emitChange();
      return;
    }

    const searchText = (searchInput.value || '').trim();

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
      searchDebounceTimer = null;
    }

    if (!searchText) {
      searchRequestId += 1;
      searchMatchIds = null;
      emitChange();
      return;
    }

    const execute = () => {
      searchRequestId += 1;
      const requestId = searchRequestId;
      const term = (searchInput.value || '').trim();

      if (!term) {
        searchMatchIds = null;
        emitChange();
        return;
      }

      Promise.resolve(search.fetchMatchIds(term))
        .then((ids) => {
          if (requestId !== searchRequestId) {
            return;
          }
          searchMatchIds = new Set((ids || []).map(id => String(id)));
          emitChange();
        })
        .catch(() => {
          if (requestId !== searchRequestId) {
            return;
          }
          searchMatchIds = new Set();
          emitChange();
        });
    };

    if (immediate) {
      execute();
    } else {
      searchDebounceTimer = setTimeout(execute, searchDebounceMs);
    }
  };

  if (searchForm) {
    searchForm.addEventListener('submit', (e) => {
      e.preventDefault();
      runSearchAndApply(true);
      if (searchInput) {
        searchInput.blur();
      }
    });
  }

  if (searchClearBtn) {
    searchClearBtn.addEventListener('click', () => {
      if (searchInput) {
        searchInput.value = '';
      }
      updateSearchClearVisibility();
      runSearchAndApply(true);
      if (searchInput) {
        searchInput.focus();
      }
    });
  }

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      updateSearchClearVisibility();
      runSearchAndApply(false);
    });

    searchInput.addEventListener('search', () => {
      updateSearchClearVisibility();
      runSearchAndApply(true);
    });

    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        runSearchAndApply(true);
        searchInput.blur();
      }
    });
  }

  const setFilters = (nextFilters) => {
    clearAllFilterCheckboxes();
    filters = emptyFilters();

    groupKeys.forEach((key) => {
      const values = (nextFilters && nextFilters[key]) || [];
      values.forEach((value) => {
        const desktopInput = getDesktopFilterOption(key, value);
        const mobileInput = getMobileFilterOption(key, value);
        if (desktopInput) {
          desktopInput.checked = true;
        }
        if (mobileInput) {
          mobileInput.checked = true;
        }
        filters[key].push(String(value));
      });
    });

    syncPendingFromApplied();
    emitChange();
  };

  /**
   * Show/hide options for one filter group. Pass null/undefined for allowedValues
   * to show all options. Invalid checked values are unchecked and removed from
   * internal state (unless pendingOnly). Does not emit onFiltersChange.
   *
   * pendingOnly: update mobile option visibility/checks only — used while the
   * mobile panel is open before Apply, so applied desktop state stays intact.
   */
  const setOptionVisibility = (groupKey, allowedValues, options) => {
    const pendingOnly = !!(options && options.pendingOnly);
    const allowAll = allowedValues == null;
    const allowedList = [];

    if (!allowAll && allowedValues) {
      if (typeof allowedValues.forEach === 'function') {
        allowedValues.forEach((value) => {
          allowedList.push(String(value));
        });
      } else {
        Array.prototype.slice.call(allowedValues).forEach((value) => {
          allowedList.push(String(value));
        });
      }
    }

    const allowedSet = allowAll ? null : new Set(allowedList);

    let cleared = false;

    const applyToInput = (input) => {
      if (input.dataset.filter !== groupKey) {
        return;
      }

      if (pendingOnly && !input.classList.contains('filter-bar-option-mobile')) {
        return;
      }

      const formCheck = input.closest('.form-check');
      const isAllowed = allowAll || allowedSet.has(String(input.value));

      if (formCheck) {
        formCheck.classList.toggle('d-none', !isAllowed);
      }

      if (!isAllowed && input.checked) {
        input.checked = false;
        if (!pendingOnly) {
          cleared = true;
        }
      }
    };

    if (!pendingOnly) {
      desktopFilterOptions.forEach(applyToInput);
    }
    mobileFilterOptions.forEach(applyToInput);

    if (!pendingOnly && filters[groupKey]) {
      const before = filters[groupKey].length;
      filters[groupKey] = allowAll
        ? filters[groupKey]
        : filters[groupKey].filter(value => allowedSet.has(String(value)));
      if (filters[groupKey].length !== before) {
        cleared = true;
      }
    }

    if (cleared) {
      renderSelectedFilters();
      updateClearAllVisibility();
    }

    const snapshot = emptyFilters();
    groupKeys.forEach((key) => {
      snapshot[key] = (filters[key] || []).slice();
    });

    return {
      cleared,
      filters: snapshot,
    };
  };

  // Start from a clean checkbox state so browser form restoration cannot
  // leave filters checked without matching applied view / URL.
  clearAllFilterCheckboxes();
  filters = emptyFilters();
  if (urlSync) {
    restoreFiltersFromQuery();
  } else {
    rebuildFiltersFromDesktop();
  }
  updateSearchClearVisibility();

  const applyInitialState = () => {
    const initialSearch = searchInput ? (searchInput.value || '').trim() : '';
    if (initialSearch) {
      runSearchAndApply(true);
    } else {
      emitChange();
    }
  };

  applyInitialState();

  // Late form restore / bfcache can re-check boxes after init. Re-apply from URL.
  if (urlSync) {
    window.addEventListener('pageshow', () => {
      restoreFiltersFromQuery();
      applyInitialState();
    });
  }

  return {
    getState: () => {
      const snapshot = emptyFilters();
      groupKeys.forEach((key) => {
        snapshot[key] = (filters[key] || []).slice();
      });
      return snapshot;
    },
    setState: setFilters,
    setOptionVisibility,
    resolveOptionValue,
    clear: clearAppliedFilters,
    hasActiveFilters: hasActiveCategoryFilters,
    buildFilterQueryString,
    getSearchText: () => (searchInput ? (searchInput.value || '').trim() : ''),
  };
};

export default createSharedFilterBar;
export { createSharedFilterBar };
